/**
 * DNACore V4 — Spotlight V4 (Onda 8)
 *
 * Command Palette ⌘K dedicado e leve para uso nas páginas V2 (Layout2).
 * - Escuta evento global `dna:open-search` (disparado pelo DnaHeaderV4).
 * - Atalho ⌘K / Ctrl+K binding nativo (failsafe se Layout não estiver com header).
 * - Reusa o endpoint existente `/dnacore/v3/spotlight`.
 * - Tem Quick Actions para navegação rápida.
 * - Look V4 premium (glass dark panel, kbd hints).
 */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, X, ArrowRight, Sparkles, Users, GraduationCap, Wallet,
  MessageSquare, BarChart3, Calendar, FileText, Building2, BrainCircuit, Settings,
  CornerDownLeft, ArrowUpDown,
} from 'lucide-react';
import api from '../lib/api';
import './DnaSpotlightV4.css';

// ─── Quick actions (atalhos para rotas V4 frequentes) ───
const QUICK_ACTIONS = [
  { id: 'qa-dash',     label: 'Ir para Dashboard',       sub: '/v2/dashboard',                       icon: BarChart3,    path: '/v2/dashboard' },
  { id: 'qa-aluno360', label: 'Aluno 360',               sub: 'Ficha + histórico + cobrança',        icon: Users,        path: '/v2/aluno' },
  { id: 'qa-academ',   label: 'Acadêmico — Turmas',      sub: '/v2/academico/turmas',                icon: GraduationCap,path: '/v2/academico/turmas' },
  { id: 'qa-notas',    label: 'Central de Notas',        sub: '/v2/academico/notas',                 icon: FileText,     path: '/v2/academico/notas' },
  { id: 'qa-pend',     label: 'Pendências Acadêmicas',   sub: '/v2/academico/pendencias',            icon: Sparkles,     path: '/v2/academico/pendencias' },
  { id: 'qa-dnapay',   label: 'DNAPay — Títulos',        sub: '/v2/financeiro/dnapay/titulos',       icon: Wallet,       path: '/v2/financeiro/dnapay/titulos' },
  { id: 'qa-remessa',  label: 'Gerar Remessa CNAB',      sub: '/v2/financeiro/dnapay/remessas',      icon: Wallet,       path: '/v2/financeiro/dnapay/remessas' },
  { id: 'qa-comunic',  label: 'Central de Comunicação',  sub: '/v2/comunicacao/mensagens',           icon: MessageSquare,path: '/v2/comunicacao/mensagens' },
  { id: 'qa-eventos',  label: 'Eventos · Festa Junina',  sub: '/v2/festa-junina',                    icon: Calendar,     path: '/v2/festa-junina' },
  { id: 'qa-ia',       label: 'IA · Painel',             sub: '/v2/ia',                              icon: BrainCircuit, path: '/v2/ia' },
  { id: 'qa-config',   label: 'Configurações',           sub: '/v2/configuracoes/sistema',           icon: Settings,     path: '/v2/configuracoes/sistema' },
];

const TYPE_META = {
  student:     { label: 'Aluno',         icon: Users },
  responsible: { label: 'Responsável',   icon: Users },
  class:       { label: 'Turma',         icon: GraduationCap },
  teacher:     { label: 'Professor',     icon: Users },
  event:       { label: 'Evento',        icon: Calendar },
  invoice:     { label: 'Título',        icon: Wallet },
  contract:    { label: 'Contrato',      icon: FileText },
  unit:        { label: 'Unidade',       icon: Building2 },
};

export default function DnaSpotlightV4() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const cacheRef = useRef(new Map());

  // ─── Open/close listeners ───
  useEffect(() => {
    const onEv = () => setOpen(true);
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('dna:open-search', onEv);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('dna:open-search', onEv);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // ─── Reset on open ───
  useEffect(() => {
    if (open) {
      setQ('');
      setItems([]);
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // ─── Debounced fetch ───
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (!term) {
      setItems([]);
      setLoading(false);
      return;
    }
    const cached = cacheRef.current.get(term);
    const now = Date.now();
    if (cached && now - cached.ts < 30000) {
      setItems(cached.data);
      setActive(0);
      return;
    }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`/dnacore/v3/spotlight?q=${encodeURIComponent(term)}&limit=10`);
        const data = r.data.items || [];
        cacheRef.current.set(term, { ts: now, data });
        setItems(data);
        setActive(0);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(debounceRef.current);
  }, [q, open]);

  // ─── Filtered quick actions ───
  const filteredQA = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return QUICK_ACTIONS.slice(0, 6);
    return QUICK_ACTIONS.filter(
      (qa) =>
        qa.label.toLowerCase().includes(term) ||
        qa.sub.toLowerCase().includes(term)
    ).slice(0, 6);
  }, [q]);

  // ─── Merge list: results first, then quick actions ───
  const list = useMemo(() => {
    const out = [];
    items.forEach((it) => out.push({ kind: 'result', data: it }));
    filteredQA.forEach((qa) => out.push({ kind: 'qa', data: qa }));
    return out;
  }, [items, filteredQA]);

  // ─── Keyboard navigation ───
  const onKeyInput = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(list.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const sel = list[active];
      if (sel) handlePick(sel);
    }
  };

  const handlePick = (entry) => {
    if (entry.kind === 'qa') {
      navigate(entry.data.path);
    } else {
      const it = entry.data;
      const map = {
        student:     `/v2/aluno?id=${it.id}`,
        responsible: `/v2/aluno?id=${it.id}`,
        class:       `/v2/academico/turmas`,
        teacher:     `/v2/secretaria`,
        event:       `/v2/festa-junina`,
        invoice:     `/v2/financeiro/dnapay/titulos`,
        contract:    `/v2/financeiro`,
      };
      const path = map[it.type] || '/v2/dashboard';
      if (it.type === 'student' || it.type === 'responsible') {
        window.dispatchEvent(new CustomEvent('dna:open-student', { detail: { id: it.id } }));
      } else {
        navigate(path);
      }
    }
    // Persiste termo recente
    if (q.trim()) {
      api.post('/dnacore/v3/prefs/search', { q: q.trim() }).catch(() => {});
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="dna4-spot-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      data-testid="dna4-spot-overlay"
    >
      <div className="dna4-spot" role="dialog" aria-modal data-testid="dna4-spot">
        {/* INPUT BAR */}
        <div className="dna4-spot-bar">
          <Search className="lead" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyInput}
            placeholder="Pesquisar alunos, turmas, títulos... ou navegar para qualquer tela"
            data-testid="dna4-spot-input"
          />
          <span className="kbd">⌘K</span>
          <button
            className="close-x"
            onClick={() => setOpen(false)}
            data-testid="dna4-spot-close"
            aria-label="Fechar"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* BODY */}
        <div className="dna4-spot-body" data-testid="dna4-spot-body">
          {loading && (
            <div className="dna4-spot-empty">
              <div className="big">⏳</div>
              <div>Pesquisando…</div>
            </div>
          )}

          {/* Resultados de busca */}
          {!loading && items.length > 0 && (
            <>
              <div className="dna4-spot-section">Resultados</div>
              {items.map((it, i) => {
                const meta = TYPE_META[it.type] || { label: 'Item', icon: ArrowRight };
                const Ico = meta.icon;
                return (
                  <button
                    key={`r-${it.id || it.label}-${i}`}
                    className={`dna4-spot-item ${active === i ? 'active' : ''}`}
                    onClick={() => handlePick({ kind: 'result', data: it })}
                    onMouseEnter={() => setActive(i)}
                    data-testid={`dna4-spot-result-${i}`}
                  >
                    <div className="ph"><Ico className="w-4 h-4" /></div>
                    <div className="body">
                      <div className="label">{it.label}</div>
                      {it.sub && <div className="sub">{it.sub}</div>}
                    </div>
                    <span className="tag">{meta.label}</span>
                  </button>
                );
              })}
            </>
          )}

          {/* Quick actions */}
          {!loading && filteredQA.length > 0 && (
            <>
              <div className="dna4-spot-section">{items.length > 0 ? 'Sugestões' : 'Ações Rápidas'}</div>
              {filteredQA.map((qa, i) => {
                const Ico = qa.icon;
                const idx = items.length + i;
                return (
                  <button
                    key={qa.id}
                    className={`dna4-spot-item ${active === idx ? 'active' : ''}`}
                    onClick={() => handlePick({ kind: 'qa', data: qa })}
                    onMouseEnter={() => setActive(idx)}
                    data-testid={`dna4-spot-qa-${qa.id}`}
                  >
                    <div className="ph"><Ico className="w-4 h-4" /></div>
                    <div className="body">
                      <div className="label">{qa.label}</div>
                      <div className="sub">{qa.sub}</div>
                    </div>
                    <span className="tag">IR</span>
                  </button>
                );
              })}
            </>
          )}

          {/* Empty (busca sem resultado) */}
          {!loading && q.trim() && items.length === 0 && filteredQA.length === 0 && (
            <div className="dna4-spot-empty" data-testid="dna4-spot-empty">
              <div className="big">🔎</div>
              <div>Nenhum resultado para <strong style={{ color: 'var(--dna-text)' }}>&quot;{q}&quot;</strong></div>
              <div style={{ marginTop: 6, fontSize: 11.5 }}>
                Tente nome, CPF, telefone, turma ou número de boleto.
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="dna4-spot-foot">
          <div className="hints">
            <span className="hint"><kbd><ArrowUpDown className="w-2.5 h-2.5" /></kbd> navegar</span>
            <span className="hint"><kbd><CornerDownLeft className="w-2.5 h-2.5" /></kbd> abrir</span>
            <span className="hint"><kbd>Esc</kbd> fechar</span>
          </div>
          <div className="brand"><Sparkles className="w-3 h-3" /> DNACore V4 Spotlight</div>
        </div>
      </div>
    </div>
  );
}
